<template>
  <div>
    <div class="custos-resumo" v-if="!loading">
      <div class="custos-resumo__card">
        <div class="custos-resumo__label-row">
          <label class="custos-resumo__label">Custo Previsto</label>
          <GraficoInfoIcon titulo="Custo Previsto"
            descricao="Valor definido na estimativa de custo do Projeto (Proposta de Criação de Mercado). Representa o orçamento aprovado para o projeto."
            formula="Soma dos custos previstos das entregas do Projeto" />
        </div>
        <span class="custos-resumo__valor">
          <span v-if="podeVerOrcamento"> {{ formatarValor(custoPrevisto) }} </span>
          <span v-else> ●●●●● </span>
        </span>
        <p class="custos-resumo__legenda custos-resumo__legenda--vazia" aria-hidden="true">.</p>
      </div>
      <div v-if="projetoId" class="custos-resumo__card">
        <div class="custos-resumo__label-row">
          <label class="custos-resumo__label">Custo Realizado</label>
          <GraficoInfoIcon titulo="Custo Realizado"
            descricao="Soma do que já foi pago (itens entregues ou concluídos), do que ainda será pago (cotações aprovadas em andamento) e das despesas lançadas do projeto."
            formula="Valor pago + Valor a pagar + Despesas" />
        </div>
        <span class="custos-resumo__valor">
          <span v-if="podeVerOrcamento">{{ formatarValor(totalRealizado) }}</span>
          <span v-else>●●●●●</span>
        </span>
        <p v-if="podeVerOrcamento" class="custos-resumo__legenda">
          <span class="custos-resumo__legenda-item">{{ formatarValor(custoReal) }} pago</span>
          <span class="custos-resumo__legenda-sep">·</span>
          <span class="custos-resumo__legenda-item">{{ formatarValor(custoComprometido) }} a pagar</span>
          <template v-if="custoDespesas > 0">
            <span class="custos-resumo__legenda-sep">·</span>
            <span class="custos-resumo__legenda-item">{{ formatarValor(custoDespesas) }} despesas</span>
          </template>
        </p>
        <p v-else class="custos-resumo__legenda">●●●●●</p>
      </div>
      <div v-else class="custos-resumo__card">
        <div class="custos-resumo__label-row">
          <label class="custos-resumo__label">Custo Realizado</label>
          <GraficoInfoIcon titulo="Custo Realizado"
            descricao="Valor pago com base nas estimativas dos itens de demanda do setor."
            formula="Σ (estimativa × quantidade)" />
        </div>
        <span class="custos-resumo__valor">
          <span v-if="podeVerOrcamento"> {{ formatarValor(custoReal) }} </span>
          <span v-else> ●●●●● </span>
        </span>
        <p class="custos-resumo__legenda custos-resumo__legenda--vazia" aria-hidden="true">.</p>
      </div>
      <div class="custos-resumo__card" :class="{ 'custos-resumo__card--negativo': saldo < 0 }">
        <div class="custos-resumo__label-row">
          <label class="custos-resumo__label">Saldo Restante</label>
          <GraficoInfoIcon titulo="Saldo Restante"
            descricao="Orçamento ainda disponível após descontar o custo realizado (valor pago + valor a pagar + despesas lançadas)."
            formula="Custo previsto − Custo realizado" />
        </div>
        <span class="custos-resumo__valor">
          <span v-if="podeVerOrcamento"> {{ formatarValor(saldo) }} </span>
          <span v-else> ●●●●● </span>
        </span>
        <p class="custos-resumo__legenda custos-resumo__legenda--vazia" aria-hidden="true">.</p>
      </div>
    </div>
    <div class="alinha-centro fonte-fraca" v-if="!podeVerOrcamento"> Os valores serão exibidos somente para membros do
      comitê de aprovação! </div>
    <div v-if="isAuditor && projetoId && !loading && !propReadOnly" class="bloqueio-demandas-bar">
      <button class="acao-secundaria" :title="tituloBloqueioDemandas"
        :style="{ 'background-color': (demandasBloqueadas ? 'var(--cor-erro)' : 'var(--cor-sucesso)') }"
        :class="demandasBloqueadas ? 'bg-erro' : 'bg-sucesso'"
        :disabled="bloqueioDemandasDesabilitado"
        @click="alternarBloqueioDemandas">
        <i :class="demandasBloqueadas ? 'bi-lock-fill' : 'bi-unlock-fill'"></i> {{ textoBloqueioDemandas }} </button>
    </div>
    <div class="bloco margem">
      <div class="grid-1">
        <div class="table-container">
          <table class="tabela tabela-orcamento" v-if="!loading">
            <colgroup>
              <col class="col-demanda" />
              <col class="col-item" />
              <col class="col-qtd" />
              <col class="col-valor" />
              <col class="col-valor" />
              <col class="col-consumidores" />
              <col class="col-motivacao" />
              <col class="col-entrega" />
              <col class="col-status" />
              <col class="col-acao" />
            </colgroup>
            <tbody>
              <tr>
                <th>Demanda</th>
                <th>Item</th>
                <th>Qtd</th>
                <th>Valor Unitário</th>
                <th>Valor Total</th>
                <th>Consumidor(es)</th>
                <th>Motivação</th>
                <th>Pacote de trabalho</th>
                <th>Status</th>
                <th></th>
              </tr>
              <template v-for="(item, index) in itens" :key="item.id ?? index">
                <tr class="linha-item" @click="selecionarItem(item)"
                  :style="{ 'background-color': (itemSelecionado.id === item.id) ? 'var(--cor-primaria-fraca)' : '' }">
                  <td class="celula-demanda" :title="item.demanda?.codigo ?? undefined"> {{ item.demanda?.codigo ?? '-'
                    }} </td>
                  <td class="desc celula-item">
                    <span v-if="codigoItem(item)">{{ codigoItem(item) }} -</span> {{ descricaoItem(item) }}
                  </td>
                  <td class="celula-qtd">{{ quantidadeOrcamentoItem(item) }}</td>
                  <td class="celula-valor">
                    <span v-if="podeVerOrcamento"> {{ formatarValor(valorUnitarioItem(item)) }} </span>
                    <span v-else> ●●●●● </span>
                  </td>
                  <td class="celula-valor">
                    <span v-if="podeVerOrcamento"> {{ formatarValor(valorTotalItem(item)) }} </span>
                    <span v-else> ●●●●● </span>
                  </td>
                  <td class="consumidores-cell">
                    <template v-if="!item.consumidores?.length"> - </template>
                    <template v-else>
                      <span v-if="estaExpandido(item, index)"> {{ consumidoresTexto(item, index, true) }} </span>
                      <span v-else> {{ consumidoresTexto(item, index, false) }} </span>
                      <button v-if="precisaBotaoExibirTodos(item)" type="button" class="btn-exibir-consumidores"
                        @click.stop="toggleExibirTodos(item, index)"> {{ estaExpandido(item, index) ? 'Ocultar' :
                          'Exibir todos' }} </button>
                    </template>
                  </td>
                  <td>{{ item.motivacao?.nome ?? '-' }}</td>
                  <td class="celula-entrega" @click.stop>
                    <template v-if="podeEditarEntrega">
                      <select class="select-entrega" :value="String(item.entrega_projeto?.id ?? '')"
                        :disabled="salvandoEntregaItemId === item.id" @change="onEntregaChange(item, $event)">
                        <option value="">— Sem pacote de trabalho —</option>
                        <option v-for="e in entregasProjeto" :key="e.id" :value="String(e.id)"> {{ e.nome || 'Sem nome'
                        }} </option>
                      </select>
                    </template>
                    <template v-else>
                      <span class="desc">{{ item.entrega_projeto?.nome ?? '-' }}</span>
                    </template>
                  </td>
                  <td><span class="chip" :class="corStatus(item.status)"> {{ formatarStatus(item.status) }} </span>
                  </td>
                  <td></td>
                </tr>
                <tr v-if="itemSelecionado.id === item.id && !loading" class="timeline-row">
                  <td :colspan="colunasTabela" class="timeline-cell">
                    <div class="timeline-scroll">
                      <TimeLine v-if="registros" :propItem="registros" />
                      <div v-else-if="carregandoTimeline" class="timeline-loading">
                        <div class="loading">
                          <div></div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <div v-else class="loading">
            <div></div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="projetoId && !loading" class="bloco margem bloco-despesas">
      <div class="grid-1">
        <h3 class="titulo-secao-despesas">Despesas do Projeto</h3>
        <div class="table-container">
          <table class="tabela tabela-orcamento">
            <tbody>
              <tr>
                <th>Data</th>
                <th>Fornecedor</th>
                <th>Motivo</th>
                <th>Responsável</th>
                <th>Valor</th>
              </tr>
              <tr v-if="despesas.length === 0">
                <td colspan="5" class="alinha-centro fonte-fraca">Nenhuma despesa lançada para este projeto.</td>
              </tr>
              <tr v-for="despesa in despesas" :key="despesa.id" class="linha-despesa" @click="abrirVisualizacaoDespesa(despesa)">
                <td>{{ formatarData(despesa.data) }}</td>
                <td>{{ despesa.fornecedor || '-' }}</td>
                <td>{{ despesa.motivacao?.nome || '-' }}</td>
                <td>{{ despesa.responsavel?.name || '-' }}</td>
                <td class="celula-valor">
                  <span v-if="podeVerOrcamento">{{ formatarValor(despesa.valor) }}</span>
                  <span v-else>●●●●●</span>
                </td>
              </tr>
              <tr v-if="despesas.length > 0" class="linha-total-despesas">
                <td colspan="3"></td>
                <td class="celula-total-label"><strong>Total</strong></td>
                <td class="celula-valor">
                  <strong v-if="podeVerOrcamento">{{ formatarValor(custoDespesas) }}</strong>
                  <strong v-else>●●●●●</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <ModalVisualizarDespesaProjeto
    :mostrar="mostrarVisualizacaoDespesa"
    :despesa="despesaVisualizacao"
    :mostrar-projeto="false"
    :pode-ver-valor="podeVerOrcamento"
    @fechar="fecharVisualizacaoDespesa"
  />
</template>
<script>
import TimeLine from '@/components/TimeLine/TimeLine.vue';
import GraficoInfoIcon from '@/components/Graficos/GraficoInfoIcon.vue';
import ModalVisualizarDespesaProjeto from '@/components/Modais/projetos/ModalVisualizarDespesaProjeto.vue';
import { useApiError } from "@/composables/useApiError";
import serviceProjetos from "@/services/serviceProjetos";

const STATUS_EXCLUIDOS_ORCAMENTO = [
  'rejeitado',
  'Cancelado',
  'cancelado',
  'Reprovado',
  'reprovado',
  'Desautorizado',
  'nao_recebido',
];

export default {
  name: "OrcamentoComponent",

  components: {
    TimeLine,
    GraficoInfoIcon,
    ModalVisualizarDespesaProjeto,
  },

  setup() {
    const { handleError } = useApiError();
    return { handleApiError: handleError };
  },

  props: {
    propPCM: {
      required: true
    },
    projetoId: {
      required: false
    },
    propSetoresId: {
      required: false
    },
    podeVerOrcamento: {
      required: true
    },
    setores: {
      required: true
    },
    propReadOnly: {
      required: true
    },
    /** Só no frontend: define se o select de entrega aparece (gerente do projeto / PCM). */
    isGerente: {
      type: Boolean,
      default: false,
    },
    isAuditor: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['demandas-bloqueadas-change'],

  data() {
    return {
      projeto: {},
      itens: [],
      itemSelecionado: {},
      registros: null,
      loading: true,
      itensExpandidos: {}, // { [itemKey]: true } para controle de "Exibir todos"
      limiteConsumidoresVisiveis: 3,
      entregasProjeto: [],
      salvandoEntregaItemId: null,
      custoReal: 0,
      custoComprometido: 0,
      custoDespesas: 0,
      despesas: [],
      mostrarVisualizacaoDespesa: false,
      despesaVisualizacao: null,
      orcamentoDetalhado: null,
      salvandoBloqueioDemandas: false,
      carregandoTimeline: false,
      colunasTabela: 10,
    }
  },
  computed: {
    projetoEmAndamento() {
      return this.projeto?.status === 'Em andamento';
    },
    demandasBloqueadas() {
      if (this.projeto?.status && !this.projetoEmAndamento) {
        return true;
      }
      return Boolean(this.projeto?.demandas_bloqueadas);
    },
    bloqueioDemandasDesabilitado() {
      return this.salvandoBloqueioDemandas
        || (this.demandasBloqueadas && !this.projetoEmAndamento);
    },
    tituloBloqueioDemandas() {
      if (this.demandasBloqueadas && !this.projetoEmAndamento) {
        return 'Só é possível liberar demandas em projetos Em andamento';
      }
      return this.demandasBloqueadas ? 'Liberar' : 'Bloquear';
    },
    podeEditarEntrega() {
      return Boolean(this.isGerente && this.projetoId && !this.propReadOnly);
    },
    custoPrevisto() {
      const resumo = this.orcamentoDetalhado?.resumo;
      if (resumo?.custo_previsto != null) {
        return Number(resumo.custo_previsto);
      }
      return Number(this.propPCM?.estimativa_custo) || 0;
    },
    totalRealizado() {
      const resumo = this.orcamentoDetalhado?.resumo;
      if (resumo?.gasto_atual != null) {
        const gasto = Number(resumo.gasto_atual);
        if (!Number.isNaN(gasto)) {
          return gasto;
        }
      }
      return (Number(this.custoReal) || 0) + (Number(this.custoComprometido) || 0) + (Number(this.custoDespesas) || 0);
    },
    saldo() {
      const resumo = this.orcamentoDetalhado?.resumo;
      if (resumo?.saldo != null) {
        return Number(resumo.saldo);
      }
      return this.custoPrevisto - this.totalRealizado;
    },
    textoBloqueioDemandas() {
      return this.demandasBloqueadas ? 'Demandas Bloqueadas' : 'Demandas Liberadas';
    },
  },
  async mounted() {
    await this.getProjeto();
    if (this.podeEditarEntrega) {
      await this.carregarEntregasProjeto();
    }
    await this.getDemandas();
  },
  watch: {
    'itemSelecionado.id'(novo, antigo) {
      if (novo && novo !== antigo) {
        this.getTimeLine();
      }
    }
  },

  methods: {
    chaveItem(item, index) {
      return item?.id ?? `idx-${index}`;
    },
    selecionarItem(item) {
      this.itemSelecionado = (this.itemSelecionado?.id === item.id) ? {} : item;
    },
    encurtarNome(nomeCompleto) {
      if (!nomeCompleto || typeof nomeCompleto !== 'string') return nomeCompleto ?? '-';
      const partes = nomeCompleto.trim().split(/\s+/).filter(Boolean);
      if (partes.length <= 2) return nomeCompleto.trim();
      return `${partes[0]} ${partes[partes.length - 1]}`;

    },
    consumidoresTexto(item, index, expandido) {
      const list = item.consumidores?.map(c => c?.name).filter(Boolean) ?? [];
      const nomes = list.map(n => this.encurtarNome(n));
      const limite = this.limiteConsumidoresVisiveis;
      if (expandido || list.length <= limite) {
        return nomes.join(', ');
      }
      const visiveis = nomes.slice(0, limite);
      const restante = list.length - limite;
      return visiveis.join(', ') + `, +${restante}`;
    },
    precisaBotaoExibirTodos(item) {
      const list = item.consumidores ?? [];
      return list.length > this.limiteConsumidoresVisiveis;
    },
    estaExpandido(item, index) {
      return !!this.itensExpandidos[this.chaveItem(item, index)];
    },
    toggleExibirTodos(item, index) {
      const key = this.chaveItem(item, index);
      this.itensExpandidos = {
        ...this.itensExpandidos,
        [key]: !this.itensExpandidos[key],
      };
    },
    formatarValor(valor) {
      if (valor == null || Number.isNaN(Number(valor))) {
        return '-';
      }
      return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    formatarData(data) {
      if (!data) return '-';
      const [ano, mes, dia] = String(data).slice(0, 10).split('-');
      if (!ano || !mes || !dia) return data;
      return `${dia}/${mes}/${ano}`;
    },
    abrirVisualizacaoDespesa(despesa) {
      this.despesaVisualizacao = { ...despesa };
      this.mostrarVisualizacaoDespesa = true;
    },
    fecharVisualizacaoDespesa() {
      this.mostrarVisualizacaoDespesa = false;
      this.despesaVisualizacao = null;
    },
    quantidadeOrcamentoItem(item) {
      return item.qt_comprar ?? item.qt_solicitada ?? '-';
    },
    temCotacaoAprovada(item) {
      return item.cotacao_aprovada?.valor_unitario != null;
    },
    itemExcluidoDoOrcamento(item) {
      return STATUS_EXCLUIDOS_ORCAMENTO.includes(item?.status);
    },
    valorUnitarioItem(item) {
      if (this.itemExcluidoDoOrcamento(item)) {
        return null;
      }
      if (this.temCotacaoAprovada(item)) {
        return Number(item.cotacao_aprovada.valor_unitario);
      }
      return null;
    },
    valorTotalItem(item) {
      if (this.itemExcluidoDoOrcamento(item)) {
        return null;
      }
      if (item.valor_total_cotacao_aprovada != null) {
        return Number(item.valor_total_cotacao_aprovada);
      }
      if (this.temCotacaoAprovada(item)) {
        const quantidade = Number(item.qt_comprar ?? item.qt_solicitada) || 0;
        const unitario = Number(item.cotacao_aprovada.valor_unitario) || 0;
        const frete = Number(item.cotacao_aprovada.frete) || 0;
        return (unitario * quantidade) + frete;
      }
      return null;
    },
    aplicarCotacaoAprovadaNosItens() {
      const porId = new Map(
        (this.orcamentoDetalhado?.itens ?? []).map((i) => [i.dem_demanda_item_id, i]),
      );
      if (porId.size === 0) return;
      this.itens = this.itens.map((item) => {
        const detalhe = porId.get(item.id);
        if (!detalhe) return item;
        return {
          ...item,
          cotacao_aprovada: detalhe.cotacao_aprovada,
          valor_total_cotacao_aprovada: detalhe.valor_total_cotacao_aprovada,
        };
      });
    },
    async carregarOrcamentoDetalhado() {
      if (!this.projetoId) return;
      try {
        const data = await serviceProjetos.getOrcamentoDetalhadoProjeto(this.projetoId);
        this.orcamentoDetalhado = data;
        if (data?.resumo) {
          this.custoReal = Number(data.resumo.custo_real ?? this.custoReal);
          this.custoComprometido = Number(data.resumo.custo_comprometido_compras ?? 0);
          this.custoDespesas = Number(data.resumo.custo_despesas ?? 0);
        }
        this.despesas = Array.isArray(data?.despesas) ? data.despesas : [];
        this.aplicarCotacaoAprovadaNosItens();
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Orçamento detalhado indisponível, usando fallback.', error);
        }
        this.orcamentoDetalhado = null;
        this.custoComprometido = 0;
        this.custoDespesas = 0;
        this.despesas = [];
      }
    },
    async getProjeto() {
      if (this.projetoId) {
        this.projeto = await serviceProjetos.getProjetoById(this.projetoId);
        const bloqueadas = Boolean(this.projeto?.demandas_bloqueadas)
          || (this.projeto?.status && this.projeto.status !== 'Em andamento');
        this.$emit('demandas-bloqueadas-change', bloqueadas);
      }
    },
    async alternarBloqueioDemandas() {
      if (this.propReadOnly) return
      if (!this.projetoId || !this.isAuditor) return;
      if (this.bloqueioDemandasDesabilitado) return;
      this.salvandoBloqueioDemandas = true;
      const novoValor = !this.demandasBloqueadas;
      try {
        await serviceProjetos.atualizarProjeto(this.projetoId, {
          demandas_bloqueadas: novoValor,
        });
        this.projeto = { ...this.projeto, demandas_bloqueadas: novoValor };
        this.$emit('demandas-bloqueadas-change', novoValor);
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.salvandoBloqueioDemandas = false;
      }
    },
    async carregarEntregasProjeto() {
      if (!this.projetoId) return;
      try {
        const r = await serviceProjetos.getEntregas(this.projetoId);
        const p = r?.projetos?.[0];
        const lista = p?.entregas ?? [];
        this.entregasProjeto = [...lista].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
      } catch (error) {
        this.handleApiError(error);
        this.entregasProjeto = [];
      }
    },
    async onEntregaChange(item, event) {
      if (this.propReadOnly) return
      if (!this.podeEditarEntrega || !item?.id) return;
      const raw = event.target?.value;
      const entregaId = raw === '' || raw == null ? null : Number(raw);
      if (entregaId != null && !Number.isFinite(entregaId)) return;
      if (entregaId != null && !this.entregasProjeto.some((e) => e.id === entregaId)) {
        this.handleApiError(new Error('Pacote de trabalho inválido para este projeto.'));
        event.target.value = String(item.entrega_projeto?.id ?? '');
        return;
      }
      this.salvandoEntregaItemId = item.id;
      try {
        const res = await serviceProjetos.atualizarDemandaItemEntrega(item.id, entregaId);
        const atualizado = res?.data ?? res;
        const ep = atualizado?.entrega_projeto;
        item.entrega_projeto = ep
          ? { id: ep.id, nome: ep.nome, projeto_id: ep.projeto_id }
          : null;
      } catch (error) {
        this.handleApiError(error);
        event.target.value = String(item.entrega_projeto?.id ?? '');
      } finally {
        this.salvandoEntregaItemId = null;
      }
    },
    async getDemandas() {
      if (this.projetoId) {
        this.loading = true;
        try {
          const resultado = await serviceProjetos.getDemandasProjeto(this.projetoId);
          this.custoReal = Number(resultado.custoTotal ?? 0);
          this.itens = Array.isArray(resultado) ? resultado : [];
          await this.carregarOrcamentoDetalhado();
        } catch (error) {
          this.handleApiError(error);
          this.itens = [];
          this.custoReal = 0;
          this.custoComprometido = 0;
        }
      } else if (this.propSetoresId?.length > 0) {
        this.loading = true;
        this.itens = await serviceProjetos.getDemandasSetor(this.propSetoresId);
        this.custoReal = this.itens.reduce(
          (acc, item) => acc + (Number(item.valor) || 0) * (Number(item.qt_solicitada) || 0),
          0,
        );
      }
      this.loading = false;
    },
    codigoItem(item) {
      return item.produto?.cod ?? item.servico?.codigo_servico ?? null;
    },
    descricaoItem(item) {
      return item.produto?.desc ?? item.servico?.descricao_servico ?? item.descricao ?? '-';
    },
    formatarStatus(status) {
      const statusFormatados = {
        'Pendente': 'Em análise',
        'cotacao': 'Requisição Criada',
        'aprovacao': 'Cotação Realizada',
        'rejeitado': 'Item Reprovado',
        'compra': 'Cotação Aprovada',
        'recebimento': 'Aguardando Recebimento',
        'almoxarifado': 'Chegou na empresa',
        'finalizada_almoxarifado': 'Finalizada no almoxarifado',
        'gestao': 'Aguardando Execução',
        'pagamento': 'Pagamento Solicitado',
        'pagamento_finalizado': 'Pagamento Realizado',
        'nao_recebido': 'Item não Recebido',
      };
      return statusFormatados[status] ?? status;
    },
    corStatus(status) {
      const cores = {
        'Rascunho': 'bg-ok',
        'Cadastro Pendente': 'bg-cinza',
        'Pendente': 'bg-cinza',
        'Revisão': 'bg-alerta',
        'Autorizado': 'bg-ok',
        'Desautorizado': 'bg-erro',
        'Concluído': 'bg-sucesso',
        'Concluída': 'bg-sucesso',
        'Em Separação': 'bg-ok',
        'Separado': 'bg-ok',
        'Separado Parcialmente': 'bg-ok',
        'Entregue': 'bg-sucesso',
        'Entregue Parcialmente': 'bg-ok',
        'Reprovado': 'bg-erro',
        'Cancelado': 'bg-erro',
        'Cotação': 'bg-ok',
        'Aprovado': 'bg-ok',

        // Status de compras (em minúsculas)
        'cotacao': 'bg-ok',
        'aprovacao': 'bg-ok',
        'rejeitado': 'bg-erro',
        'compra': 'bg-ok',
        'recebimento': 'bg-ok',
        'almoxarifado': 'bg-ok',
        'finalizada_almoxarifado': 'bg-sucesso',
        'gestao': 'bg-ok',
        'pagamento': 'bg-ok',
        'pagamento_finalizado': 'bg-sucesso',
        'nao_recebido': 'bg-erro',
      };
      return cores[status] ?? 'bg-ok';
    },
    async getTimeLine() {
      if (!this.itemSelecionado?.id) return
      this.carregandoTimeline = true;
      this.registros = null;
      try {
        var response = await serviceProjetos.getTimeLine(this.itemSelecionado.id);
        this.registros = response
      } catch (error) {
        this.handleApiError(error);
        this.registros = { timeline: [] };
      } finally {
        this.carregandoTimeline = false;
      }
    }
  },
}
</script>
<style scoped>
.celula-entrega {
  min-width: 10rem;
  vertical-align: middle;
}

.bloqueio-demandas-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.select-entrega {
  max-width: 14rem;
  width: 100%;
  padding: 0.35rem 0.5rem;
  font-size: 0.875rem;
  border: 1px solid var(--cor-separador);
  border-radius: 6px;
  background: var(--cor-bg);
  color: var(--cor-fonte);
}

.consumidores-cell .btn-exibir-consumidores {
  display: block;
  margin-top: 4px;
  padding: 2px 8px;
  font-size: 0.75rem;
  color: var(--cor-primaria);
  background: transparent;
  border: 1px solid var(--cor-primaria-fraca);
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
}

.consumidores-cell .btn-exibir-consumidores:hover {
  background: var(--cor-primaria-fraca);
}

.bloco.margem .grid-1 {
  min-width: 0;
  max-width: 100%;
}

.table-container {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.table-container .tabela-orcamento {
  width: 100%;
  table-layout: fixed;
}

.tabela-orcamento th,
.tabela-orcamento td {
  vertical-align: top;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.tabela-orcamento .col-demanda {
  width: 9%;
}

.tabela-orcamento .col-item {
  width: auto;
}

.tabela-orcamento .col-qtd {
  width: 5%;
}

.tabela-orcamento .col-valor {
  width: 10%;
}

.tabela-orcamento .col-consumidores {
  width: 13%;
}

.tabela-orcamento .col-motivacao {
  width: 10%;
}

.tabela-orcamento .col-entrega {
  width: 12%;
}

.tabela-orcamento .col-status {
  width: 11%;
}

.tabela-orcamento .col-acao {
  width: 1.5rem;
}

.linha-item {
  cursor: pointer;
}

.celula-demanda {
  white-space: nowrap;
  vertical-align: top;
}

.celula-item {
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  white-space: normal;
  vertical-align: top;
}

.celula-qtd {
  white-space: nowrap;
  text-align: center;
}

.celula-valor {
  white-space: nowrap;
  text-align: center;
}

.desc {
  word-wrap: break-word;
  overflow-wrap: break-word;
  text-align: left;
  line-height: 1.35;
}

.consumidores-cell {
  justify-content: center;
  display: flex;
  flex-flow: column;
  text-align: left;
  word-wrap: break-word;
  overflow-wrap: break-word;
  font-size: 0.875rem;
  max-width: 100%;
}

.timeline-row .timeline-cell {
  padding: 0;
  overflow: hidden;
  width: 100%;
  background: var(--cor-primaria-fraca);
  border-bottom: 2px solid var(--cor-separador);
}

.timeline-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}

.timeline-scroll :deep(.timeline-container) {
  padding: 1rem 1.5rem;
  width: max-content;
  min-width: 100%;
  overflow: visible;
}

.timeline-loading {
  display: flex;
  justify-content: center;
  padding: 1.5rem;
}

.custos-resumo {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
  width: 100%;
  max-width: 960px;
  margin: 0 auto 1.5rem;
}

.custos-resumo__card {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  min-height: 100%;
  padding: 0.85rem 1.25rem;
  background: var(--cor-bg);
  border: 1px solid var(--cor-separador);
  border-radius: 10px;
  box-shadow: 0 2px 8px var(--cor-cinza);
}

.custos-resumo__legenda {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.15rem 0;
  margin: 0.15rem 0 0;
  font-size: 0.75rem;
  line-height: 1.35;
  color: var(--cor-fonte-fraca);
  overflow-wrap: anywhere;
}

.custos-resumo__legenda--vazia {
  visibility: hidden;
  user-select: none;
  pointer-events: none;
}

.custos-resumo__legenda-item {
  white-space: nowrap;
}

.custos-resumo__legenda-sep {
  margin: 0 0.35rem;
  opacity: 0.6;
}

@media (max-width: 768px) {
  .custos-resumo {
    grid-template-columns: 1fr;
    max-width: 360px;
  }
}

.custos-resumo__card--negativo .custos-resumo__valor {
  color: var(--cor-erro);
}

.custos-resumo__label-row {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.custos-resumo__label-row :deep(.info-icon) {
  margin-left: 0;
  font-size: 0.95rem;
}

.custos-resumo__label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--cor-fonte-fraca);
  margin: 0;
}

.custos-resumo__valor {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--cor-fonte-forte);
}

:global(.dark) .custos-resumo__card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}

.txt-sucesso {
  box-shadow: 0 0 0 1px bg-sucesso inset !important;
}

.txt-ok {
  box-shadow: 0 0 0 1px bg-ok inset !important
}

.txt-erro {
  box-shadow: 0 0 0 1px bg-erro inset !important
}

i {
  font-size: 18px;
  cursor: pointer;
}

.bloco-despesas {
  margin-top: 1.25rem;
}

.titulo-secao-despesas {
  margin: 0 0 0.75rem;
  font-size: 1.05rem;
  font-weight: 600;
}

.linha-total-despesas td {
  border-top: 1px solid var(--cor-separador);
}

.celula-total-label {
  text-align: right;
  white-space: nowrap;
}

.linha-despesa {
  cursor: pointer;
}
</style>
